import React from 'react';
import ReactDOM from 'react-dom/client';
import {
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import {
  type ErrorComponentProps,
  RouterProvider,
  createRouter,
} from '@tanstack/react-router';
import { logspot as Logspot } from '@logspot/web';
import { routeTree } from './routeTree.gen';
import './styles/globals.css';
import 'react-tooltip/dist/react-tooltip.css';
import '@fontsource/inter/300.css';
import '@fontsource/inter/400.css';
import '@fontsource/inter/500.css';
import '@fontsource/inter/600.css';
import '@fontsource/inter/700.css';
import { WidgetMenuProvider } from '@logspot-platform/widgets';
import { IS_PRODUCTION_DEPLOY } from '@/lib/deploy-env';
import { ApiError } from '@/api';
import { useSession } from '@/lib/auth-client';
import { initBetterStack } from '@/lib/better-stack';
import { WidgetMenu } from '@/components/widgets/widget';
import { Loader } from '@/components/ui/loader';
import ErrorPage from '@/components/error-page';
import NotFoundPage from '@/components/not-found-page';
import { useAtom, getDefaultStore } from 'jotai';
import { projectIdAtom, serviceUnavailableAtom } from './state/atoms';

const store = getDefaultStore();
let unavailableCount = 0;
const UNAVAILABLE_THRESHOLD = 3;

const queryCache = new QueryCache({
  onError: (error) => {
    const isUnavailable =
      (error instanceof ApiError && error.status === 503) ||
      !(error instanceof ApiError); // network error — fetch itself failed
    if (isUnavailable) {
      unavailableCount++;
      if (unavailableCount >= UNAVAILABLE_THRESHOLD) {
        store.set(serviceUnavailableAtom, true);
      }
    }
  },
  onSuccess: () => {
    if (unavailableCount > 0) {
      unavailableCount = 0;
      store.set(serviceUnavailableAtom, false);
    }
  },
});

export const queryClient = new QueryClient({
  queryCache,
  defaultOptions: {
    queries: {
      staleTime: 10000,
      retry: (failureCount, error) => {
        // Never retry client errors (4xx) — they are not transient
        if (
          error instanceof ApiError &&
          error.status >= 400 &&
          error.status < 500
        ) {
          return false;
        }
        return failureCount < 2;
      },
    },
  },
});

const router = createRouter({
  routeTree,
  context: {
    isAuthenticated: undefined!,
    isSessionPending: undefined!,
    session: undefined!,
    projectId: undefined!,
    setProjectId: undefined!,
    queryClient,
  },
  defaultPendingComponent: () => (
    <div className="grid size-auto min-h-40">
      <Loader />
    </div>
  ),
  defaultNotFoundComponent: () => <NotFoundPage />,
  defaultErrorComponent: ({ error, reset }: ErrorComponentProps) => (
    <ErrorPage error={error} reset={reset} />
  ),
  defaultPreload: 'intent',
  // Reuse preloaded route matches for 30s (the framework default) instead of
  // re-running every matched route's beforeLoad/loader on each hover. With 0,
  // hovering a nav link re-executed the heavy, redirect-throwing _auth and
  // _side-layout beforeLoad chain as a *real* load (shared parent matches are
  // not treated as preloads), which could fire redirects and wipe caches on a
  // mere hover. Data freshness is unaffected — it is governed by each query's
  // own staleTime in React Query, not by this preload window.
  defaultPreloadStaleTime: 30_000,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

const App = () => {
  const [projectId, setProjectId] = useAtom(projectIdAtom);
  // Subscribe to the better-auth session atom here so it stays mounted for
  // the lifetime of the app — that way components in _auth's subtree using
  // useSession() share the single Better Auth fetch instead of triggering
  // their own first-mount fetch. The router-level session gate lives in
  // _auth.tsx beforeLoad (via ensureQueryData(sessionOptions())), so this App component
  // never blocks rendering of public routes (sign-in, etc.).
  const { data: session, isPending: isSessionPending } = useSession();

  // Sync the signed-in user to Logspot (external analytics SDK): identify() stitches anon
  // history + derives the company from the email domain; login fires once per browser session.
  // Logspot.init() only runs on the production deploy (routes/__root.tsx); skip the calls
  // elsewhere rather than have the SDK warn "identify() called before init()".
  const userId = session?.user?.id;
  const userEmail = session?.user?.email;
  const userName = session?.user?.name;
  React.useEffect(() => {
    if (!userEmail || !IS_PRODUCTION_DEPLOY) return;
    void Logspot.identify(userEmail, { name: userName, accountId: userId });
    try {
      if (!sessionStorage.getItem('lgspt_login_tracked')) {
        void Logspot.track({ event: 'Signed In' });
        sessionStorage.setItem('lgspt_login_tracked', '1');
      }
    } catch {
      // sessionStorage unavailable (private mode); skip the login de-dupe.
    }
  }, [userId, userEmail, userName]);

  // Memoize so the router context only changes when a value it carries
  // actually changes. A fresh function/object every render would call
  // router.update() on every render, amplifying any state churn caused by
  // loaders writing Jotai atoms (projectId/activeOrg) into a feedback loop.
  const isAuthenticated = React.useCallback(
    () => !!session?.user,
    [session],
  );

  const context = React.useMemo(
    () => ({
      queryClient,
      isAuthenticated,
      isSessionPending,
      session,
      projectId,
      setProjectId,
    }),
    [isAuthenticated, isSessionPending, session, projectId, setProjectId],
  );

  return <RouterProvider context={context} router={router} />;
};

// Expose the deployed build (version@sha) — baked into the bundle at build via
// vite.config.ts `define` — on a namespaced global for support/debugging. Silent
// (no production console litter); read on demand via `window.__LOGSPOT_ADMIN__.build`
// or curl /version.json.
const build: AppBuild = __APP_BUILD__;
window.__LOGSPOT_ADMIN__ = { build };
if (import.meta.env.DEV) console.info(`Logspot Admin ${build.display}`);

// Load Better Stack frontend tracking once the visitor grants analytics consent
// (Concord). No-op outside production and until consent is granted.
initBetterStack();

const rootElement = document.getElementById('root')!;
if (!rootElement) {
  throw new Error('Could not find root element');
}
if (!rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        {/* Inject the admin chrome (dropdown + screenshot dialog) into the
            shared widget shell from @logspot-platform/widgets. */}
        <WidgetMenuProvider
          renderMenu={(menuProps) => <WidgetMenu {...menuProps} />}
        >
          <App />
        </WidgetMenuProvider>
      </QueryClientProvider>
    </React.StrictMode>,
  );
}
